import { Form, Modal, Radio, Row, Select, Space } from 'antd';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CUSTOM_REPORT_ALERT_DURATION_MS } from '../../redux/custom-report-constant';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import 'moment/locale/th';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { RBHAlert, RBHButton, RBHDateRangePicker, RBHRadio, RBHSelect, RBHText } from '../../../../../shared/components';
import styleButton from '../../../../../shared/components/rbh-button/styles';
import './style.css';
import { Colors } from '../../../../../shared/theme';
import { reportCreators } from '../../../Report/redux/report-action';
import { REPORT_STATE_KEY, reportStateSelector } from '../../../Report/redux/report-reducer';
import { CUSTOM_REPORT_REQUEST_ID_LIST, CUSTOM_REPORT_TYPE } from '../../redux/custom-report-constant';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../../../redux/general/general-reducer';
import { customReportSagas } from '../../redux/custom-report-saga';
import { CustomReportService } from '../../redux/custom-report-service';
import styles from '../../styles';
import { DateRange } from '../../../../../shared/components/rbh-daterangepicker';

interface CreateReportModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onFailure: () => void;
}

type Shop = { shopName: string; shopId: number };
interface FormValues {
    branch: number | 'all';
    docType: CUSTOM_REPORT_TYPE;
}

interface CustomReportPayload {
    reportType: CUSTOM_REPORT_TYPE;
    startDate: string;
    endDate: string;
    shopId?: number;
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({ visible, onClose, onSuccess, onFailure }) => {
    const [form] = Form.useForm();
    const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
    const [shopListError, setShopListError] = useState(false);
    const dispatch = useDispatch();
    const { Option } = Select;

    const shopList = useSelector(reportStateSelector(REPORT_STATE_KEY.SHOP_LIST)) as Shop[] | undefined;
    const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG));
    const minDate = useMemo(() => {
        if (!Array.isArray(appConfig)) return undefined;
        const value = appConfig.find((item) => item.key === 'custom_report.enabled_date')?.value;
        return value ? moment(value) : undefined;
    }, [appConfig]);

    useEffect(() => {
        if (visible) {
            dispatch(reportCreators.callConfigAndShopList(() => {}, () => setShopListError(true)));
        }
    }, [visible, dispatch]);

    const applyDefaultDateRange = useCallback(() => {
        const yesterday = moment().subtract(1, 'day').startOf('day');
        if (minDate && minDate.isAfter(yesterday, 'day')) {
            setDateRange({ from: undefined, to: undefined });
        } else {
            setDateRange({ from: yesterday, to: yesterday });
        }
    }, [minDate]);

    useEffect(() => {
        if (!visible) return;
        applyDefaultDateRange();
    }, [visible, minDate, applyDefaultDateRange]);

    const onFinish = (values: FormValues) => {
        const { from: start, to: end } = dateRange;
        if (!start || !end) return;
        const payload: CustomReportPayload = {
            reportType: values.docType,
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
        };
        if (values.branch !== 'all') {
            payload.shopId = values.branch;
        }
        dispatch(httpCreators.request({
            id: CUSTOM_REPORT_REQUEST_ID_LIST.POST_CUSTOM_REPORT,
            sagaHandler: customReportSagas.postCustomReportHandler,
            service: CustomReportService.postCustomReport,
            serviceParams: payload,
            callbackParams: {
                onSuccess: () => {
                    onClose();
                    onSuccess();
                },
                onFailure,
            },
        }));
    };

    const onCancel = () => {
        form.resetFields();
        applyDefaultDateRange();
        onClose();
    };

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            footer={null}
            closable={false}
            maskClosable={false}
            centered
            destroyOnClose
            bodyStyle={{ backgroundColor: Colors.white, borderRadius: '8px', padding: '24px 24px 16px' }}
        >
            {shopListError && (
                <RBHAlert
                    type="error"
                    text="main.custom-report.alert.error"
                    autoCloseDuration={CUSTOM_REPORT_ALERT_DURATION_MS}
                    afterClose={() => setShopListError(false)}
                />
            )}
            <Form style={styles.modalForm} form={form} onFinish={(v) => onFinish(v as FormValues)} layout="vertical">
                <RBHText
                    data-testid="create-report-modal-title"
                    id="create-report-modal-title"
                    style={styles.modalTitle}
                    text="main.custom-report.modal.title"
                />

                <div id='create-report-date-range-container' style={{ marginBottom: 16 }}>
                    <RBHDateRangePicker
                        label={<FormattedMessage id="main.custom-report.modal.date-range.label" />}
                        value={dateRange}
                        onChange={setDateRange}
                        minDate={minDate}
                        maxDate={moment().subtract(1, 'day')}
                        maxDateRange={31}
                        quickSelect={['yesterday', 'last7days', 'monthToYesterday', 'lastMonth']}
                        style={{ width: '100%' }}
                    />
                </div>

                <Form.Item name="branch" initialValue="all" style={{ marginBottom: 24 }}>
                    <RBHSelect
                        variant="outlined"
                        label={<FormattedMessage id="main.custom-report.modal.branch.label" />}
                        style={styles.branchSelect}
                        id="create-report-branch"
                        className="create-report-branch-select"
                        dropdownClassName="create-report-branch-dropdown"
                    >
                        <Option value="all" style={styles.selectOption}>
                            <FormattedMessage id="main.custom-report.modal.branch.all" />
                        </Option>
                        {(shopList ?? []).map((shop) => (
                            <Option key={shop.shopId} value={shop.shopId} style={styles.selectOption}>
                                {shop.shopName}
                            </Option>
                        ))}
                    </RBHSelect>
                </Form.Item>

                <RBHText
                    data-testid="create-report-doc-type-label"
                    id="create-report-doc-type-label"
                    style={styles.docTypeLabel}
                    text="main.custom-report.modal.doc-type.label"
                />
                <Form.Item name="docType" initialValue={CUSTOM_REPORT_TYPE.STM} style={{ marginBottom: 24 }}>
                    <Radio.Group>
                        <RBHRadio
                            value={CUSTOM_REPORT_TYPE.STM}
                            id="create-report-doc-type-statement"
                            data-testid="create-report-doc-type-statement"
                            textOption="main.custom-report.modal.doc-type.statement"
                        />
                        <RBHRadio
                            value={CUSTOM_REPORT_TYPE.TNV}
                            id="create-report-doc-type-invoice"
                            data-testid="create-report-doc-type-invoice"
                            textOption="main.custom-report.modal.doc-type.invoice"
                        />
                    </Radio.Group>
                </Form.Item>

                <Row justify="end">
                    <Space size={12}>
                        <RBHButton
                            data-testid="create-report-cancel-btn"
                            id="create-report-cancel-btn"
                            style={styleButton.buttonModalCancel}
                            onClick={onCancel}
                            text="main.custom-report.modal.cancel"
                        />
                        <RBHButton
                            data-testid="create-report-confirm-btn"
                            id="create-report-confirm-btn"
                            style={styleButton.buttonSave}
                            type="primary"
                            htmlType="submit"
                            text="main.custom-report.modal.confirm"
                            disabled={!dateRange.from || !dateRange.to}
                        />
                    </Space>
                </Row>
            </Form>
        </Modal>
    );
};

export default CreateReportModal;
